import React from 'react';
import BigNumber from 'bignumber.js';
import { useIntl } from 'react-intl';
import { valueToBigNumber } from '@aave/protocol-js';
import { useThemeContext, BasicModal } from '@aave/aave-ui-kit';

import { useDynamicPoolDataContext, useStaticPoolDataContext } from '../../libs/pool-data-provider';
import { useProtocolDataContext } from '../../libs/protocol-data-provider';
import Row from '../basic/Row';
import Value from '../basic/Value';
import ValuePercent from '../basic/ValuePercent';
import CurrentLTVHelpModal from '../HelpModal/CurrentLTVHelpModal';
import MaxLTVHelpModal from '../HelpModal/MaxLTVHelpModal';
import Caption from '../basic/Caption';
import { getAssetColor, isAssetStable, TokenIcon } from '../../helpers/config/assets-config';

import messages from './messages';
import staticStyles from './style';
import { calculateLiquidationPrice } from './calculations';

interface LTVInfoModalProps {
  visible: boolean;
  setVisible: (value: boolean) => void;
}

export default function LTVInfoModal({ visible, setVisible }: LTVInfoModalProps) {
  const intl = useIntl();
  const { currentTheme } = useThemeContext();
  const { networkConfig } = useProtocolDataContext();
  const { marketRefPriceInUsd } = useStaticPoolDataContext();
  const { user, reserves } = useDynamicPoolDataContext();

  let liquidationPrice = '0';
  let unitPrice = '0';
  let currency = '';

  if (!user) {
    return null;
  }

  const {
    userReservesData,
    currentLoanToValue,
    currentLiquidationThreshold,
    totalBorrowsMarketReferenceCurrency,
    totalCollateralMarketReferenceCurrency,
  } = user;

  const loanToValue = valueToBigNumber(totalBorrowsMarketReferenceCurrency)
    .dividedBy(totalCollateralMarketReferenceCurrency || '1')
    .toFixed();

  const collateralComposition = userReservesData
    .filter((userReserve) => {
      const poolReserve = reserves.find((res) => res.symbol === userReserve.reserve.symbol);
      return (
        userReserve.usageAsCollateralEnabledOnUser &&
        poolReserve &&
        poolReserve.usageAsCollateralEnabled &&
        userReserve.underlyingBalance !== '0'
      );
    })
    .map((userReserve) => ({
      title: userReserve.reserve.symbol,
      color: getAssetColor(userReserve.reserve.symbol),
      value: userReserve.underlyingBalance,
      reserveId: userReserve.reserve.id,
      currentunderlyingBalanceMarketReferenceCurrency:
        userReserve.underlyingBalanceMarketReferenceCurrency,
    }));

  const borrowedReserves = user?.userReservesData
    .filter((reserve) => reserve.totalBorrows !== '0')
    .map((reserve) => ({
      ...reserve,
      title: reserve.reserve.symbol,
    }));

  const hasOneCollateralAndOneReserve =
    collateralComposition.length === 1 && borrowedReserves.length === 1;

  const collateralIsStable = hasOneCollateralAndOneReserve
    ? isAssetStable(collateralComposition[0].title)
    : false;
  const borrowIsStable = hasOneCollateralAndOneReserve
    ? isAssetStable(borrowedReserves[0].reserve.symbol)
    : false;

  const conditions =
    hasOneCollateralAndOneReserve &&
    ((collateralIsStable && !borrowIsStable) || (!collateralIsStable && borrowIsStable));

  const volatileAssetReserveId = conditions
    ? !borrowIsStable
      ? borrowedReserves[0].reserve.id
      : collateralComposition[0].reserveId
    : undefined;
  const volatileAssetReserve = reserves.find((reserve) => reserve.id === volatileAssetReserveId);
  const hasAssetPriceData = conditions && !!volatileAssetReserve;

  if (conditions && volatileAssetReserve) {
    // Calculate the liquidation price of the borrower asset if not stablecoin
    if (!borrowIsStable) {
      currency = borrowedReserves[0].title;
      unitPrice = volatileAssetReserve.priceInMarketReferenceCurrency;
      liquidationPrice = calculateLiquidationPrice({
        volatileAssetRole: 'borrow',
        variableAssetAmount: borrowedReserves[0].totalBorrows,
        counterPositionValueInMarketReferenceCurrency:
          collateralComposition[0].currentunderlyingBalanceMarketReferenceCurrency,
        liquidationThreshold: currentLiquidationThreshold,
      });
      // Calculate the liquidation price of the collateral asset if not stablecoin
    } else if (!collateralIsStable) {
      currency = collateralComposition[0].title;
      unitPrice = volatileAssetReserve.priceInMarketReferenceCurrency;
      liquidationPrice = calculateLiquidationPrice({
        volatileAssetRole: 'collateral',
        variableAssetAmount: collateralComposition[0].value,
        counterPositionValueInMarketReferenceCurrency:
          borrowedReserves[0].totalBorrowsMarketReferenceCurrency,
        liquidationThreshold: currentLiquidationThreshold,
      });
    }
  }

  const liquidationPriceUSD = new BigNumber(liquidationPrice)
    .multipliedBy(marketRefPriceInUsd)
    .toString();
  const unitPriceUsd = new BigNumber(unitPrice).multipliedBy(marketRefPriceInUsd).toString();

  return (
    <BasicModal
      isVisible={visible}
      onBackdropPress={() => setVisible(false)}
      withCloseButton={true}
      className="LTVInfoModal__wrapper"
    >
      <div className="LTVInfoModal">
        <Caption
          title={intl.formatMessage(messages.liquidationOverview)}
          description={intl.formatMessage(messages.liquidationOverviewDescription)}
          onWhiteBackground={true}
        />

        <div className="LTVInfoModal__content">
          <Row
            title={
              <CurrentLTVHelpModal
                text={intl.formatMessage(messages.currentLTV)}
                onWhiteBackground={true}
              />
            }
            withMargin={true}
          >
            <ValuePercent value={loanToValue} onWhiteBackground={true} />
          </Row>

          <Row
            title={
              <MaxLTVHelpModal
                text={intl.formatMessage(messages.maximumLTV)}
                onWhiteBackground={true}
              />
            }
            withMargin={true}
            onWhiteBackground={true}
          >
            <ValuePercent value={currentLoanToValue} onWhiteBackground={true} />
          </Row>

          <Row
            title={intl.formatMessage(messages.liquidationThreshold)}
            withMargin={hasAssetPriceData}
            onWhiteBackground={true}
          >
            <ValuePercent value={currentLiquidationThreshold} onWhiteBackground={true} />
          </Row>

          {hasAssetPriceData && (
            <>
              <Row
                title={
                  <div className="LTVInfoModal__title">
                    {intl.formatMessage(messages.currentAssetPrice)}
                    <TokenIcon tokenSymbol={currency} width={17} height={17} />
                  </div>
                }
                withMargin={true}
                onWhiteBackground={true}
              >
                <Value
                  symbol={networkConfig.baseAsset}
                  subSymbol="USD"
                  value={unitPrice}
                  subValue={unitPriceUsd}
                  maximumSubValueDecimals={10}
                  onWhiteBackground={true}
                />
              </Row>

              <Row
                title={
                  <div className="LTVInfoModal__title">
                    {intl.formatMessage(messages.assetLiquidationPrice)}
                    <TokenIcon tokenSymbol={currency} width={17} height={17} />
                  </div>
                }
                subTitle={
                  <p className="LTVInfoModal__subTitle">
                    {intl.formatMessage(!borrowIsStable ? messages.borrow : messages.collateral)}
                  </p>
                }
                onWhiteBackground={true}
              >
                <Value
                  symbol={networkConfig.baseAsset}
                  subSymbol="USD"
                  value={liquidationPrice}
                  subValue={liquidationPriceUSD}
                  maximumSubValueDecimals={10}
                  onWhiteBackground={true}
                />
              </Row>
            </>
          )}
        </div>
      </div>

      <style jsx={true} global={true}>
        {staticStyles}
      </style>
      <style jsx={true} global={true}>{`
        .LTVInfoModal {
          &__content {
            border: 1px solid ${currentTheme.darkBlue.hex};
          }
          &__subTitle {
            color: ${currentTheme.secondary.hex};
          }
        }
      `}</style>
    </BasicModal>
  );
}
